import type { DropAnimation } from "@dnd-kit/core";
import { forcedReducedMotion } from "./interaction-preferences";

/**
 * The drop settle: how a lifted thing lands.
 *
 * One critically damped spring (response 0.3s, no overshoot) drives every
 * drag release in the kit. A dragged element keeps the pointer's velocity for
 * the first frames after release and decelerates into its slot, so there is
 * no seam between "being dragged" and "landing" — the two principles Apple
 * names for fluid drag (velocity handoff, animate from the current value)
 * without a bounce, which would read as decoration on a CRM board.
 *
 * Two entry points share the spring:
 *
 * - {@link createSpringDropAnimation} — a dnd-kit `dropAnimation` for a
 *   `<DragOverlay>` whose active node stays mounted through the drop (a
 *   sortable list reordering in place). dnd-kit measures the landing slot.
 * - {@link settleOverlay} — for a surface whose landing node is a DIFFERENT
 *   element than the one lifted (a board card moving between columns is
 *   unmounted from one and mounted in another, under a new sortable id) or
 *   whose active node is a grip, not the row. The caller clones the overlay
 *   before dnd-kit unmounts it and names the landing node; the clone flies.
 *
 * Reduced motion (the OS query or the app's forced preference) skips both:
 * the element appears in its slot at once.
 */

export type Vec2 = { x: number; y: number };

const ZERO: Vec2 = { x: 0, y: 0 };

/** Critically damped: the response is the settle time, 0.3s. */
const SPRING_OMEGA = (2 * Math.PI) / 0.3;
/** x(0.4s) < 0.1% of the travel — visually settled. */
const DROP_SETTLE_MS = 400;
const FRAME_MS = 1000 / 60;
/** Release velocity above this is a throw, not a placement; clamp so a fast
 *  flick cannot carry the element clear across the board before it turns. */
const MAX_HANDOFF_VELOCITY = 1200;
/** A pointer that has not moved for this long released at rest. */
const VELOCITY_STALE_MS = 100;
/** How long a landing may take to appear before the clone gives up and fades
 *  (an optimistic cache lands in a frame; a slow host in a few). */
const LANDING_WAIT_MS = 250;

function motionReduced(): boolean {
  if (typeof window === "undefined") return false;
  return (
    forcedReducedMotion() ??
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function clampVelocity(v: number): number {
  return Math.max(-MAX_HANDOFF_VELOCITY, Math.min(MAX_HANDOFF_VELOCITY, v));
}

/** Offset from the target at time `t` (s), starting `d0` away at `v0` px/s. */
function springOffset(d0: number, v0: number, t: number): number {
  return (d0 + (v0 + SPRING_OMEGA * d0) * t) * Math.exp(-SPRING_OMEGA * t);
}

type SettleTransform = {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
};

/**
 * Keyframes for the settle, sampled per frame so a `linear` easing plays the
 * spring exactly. X and Y are independent springs — a single spring on the
 * 2D distance desyncs when the two axes carry different velocities.
 */
function springSettleKeyframes(
  from: SettleTransform,
  to: SettleTransform,
  velocity: Vec2,
): Keyframe[] {
  const dx = from.x - to.x;
  const dy = from.y - to.y;
  const vx = clampVelocity(velocity.x);
  const vy = clampVelocity(velocity.y);
  const frames = Math.round(DROP_SETTLE_MS / FRAME_MS);
  const keyframes: Keyframe[] = [];
  for (let i = 0; i <= frames; i++) {
    const p = i / frames;
    const t = p * (DROP_SETTLE_MS / 1000);
    const last = i === frames;
    const x = last ? to.x : to.x + springOffset(dx, vx, t);
    const y = last ? to.y : to.y + springOffset(dy, vy, t);
    // Scale (the overlay sized to a slot of another width) has no momentum
    // to carry; it eases along the same clock.
    const sx = from.scaleX + (to.scaleX - from.scaleX) * p;
    const sy = from.scaleY + (to.scaleY - from.scaleY) * p;
    keyframes.push({
      offset: p,
      transform: `translate3d(${x}px, ${y}px, 0) scale(${sx}, ${sy})`,
    });
  }
  return keyframes;
}

/** Plays the settle on `node`; resolves when it has landed. */
function animateSettle(
  node: HTMLElement,
  from: SettleTransform,
  to: SettleTransform,
  velocity: Vec2,
): Promise<void> {
  const keyframes = springSettleKeyframes(from, to, velocity);
  if (typeof node.animate !== "function") return Promise.resolve();
  const animation = node.animate(keyframes, {
    duration: DROP_SETTLE_MS,
    easing: "linear",
    fill: "forwards",
  });
  return new Promise((resolve) => {
    animation.onfinish = () => resolve();
    animation.oncancel = () => resolve();
  });
}

/**
 * Release-velocity tracker for a drag surface: feed it the active rect on
 * every `onDragMove`, read `velocity()` at drop. The estimate is a smoothed
 * finite difference over the last samples; a pointer that paused before
 * release reads as zero, so a careful placement lands straight.
 */
export function createVelocityTracker() {
  let last: { x: number; y: number; t: number } | null = null;
  let velocity: Vec2 = ZERO;
  return {
    reset() {
      last = null;
      velocity = ZERO;
    },
    sample(point: Vec2) {
      const t = performance.now();
      if (last) {
        const dt = (t - last.t) / 1000;
        if (dt > 0) {
          const vx = (point.x - last.x) / dt;
          const vy = (point.y - last.y) / dt;
          velocity = { x: velocity.x * 0.4 + vx * 0.6, y: velocity.y * 0.4 + vy * 0.6 };
        }
      }
      last = { x: point.x, y: point.y, t };
    },
    velocity(): Vec2 {
      if (!last || performance.now() - last.t > VELOCITY_STALE_MS) return ZERO;
      return velocity;
    },
  };
}

/**
 * A dnd-kit `dropAnimation` on the settle spring. Mirrors dnd-kit's default
 * landing math (the overlay travels to the active node's rect, scaling to
 * its size) and hides the active node while the overlay is in flight so the
 * element is never painted twice. `getVelocity` hands the release velocity
 * to the spring; omit it for a placement that always lands straight.
 */
export function createSpringDropAnimation(getVelocity?: () => Vec2): DropAnimation {
  return ({ active, dragOverlay, transform }) => {
    if (motionReduced()) return;
    const delta = {
      x: dragOverlay.rect.left - active.rect.left,
      y: dragOverlay.rect.top - active.rect.top,
    };
    const to: SettleTransform = {
      x: transform.x - delta.x,
      y: transform.y - delta.y,
      scaleX:
        transform.scaleX !== 1
          ? (active.rect.width * transform.scaleX) / dragOverlay.rect.width
          : 1,
      scaleY:
        transform.scaleY !== 1
          ? (active.rect.height * transform.scaleY) / dragOverlay.rect.height
          : 1,
    };
    const from: SettleTransform = {
      x: transform.x,
      y: transform.y,
      scaleX: transform.scaleX,
      scaleY: transform.scaleY,
    };
    if (from.x === to.x && from.y === to.y && from.scaleX === to.scaleX && from.scaleY === to.scaleY) {
      return;
    }
    const previousOpacity = active.node.style.opacity;
    active.node.style.opacity = "0";
    return animateSettle(dragOverlay.node, from, to, getVelocity?.() ?? ZERO).then(
      () => {
        active.node.style.opacity = previousOpacity;
      },
    );
  };
}

/** The shared settle for sortable lists that land straight:
 *  `<DragOverlay dropAnimation={SPRING_DROP_ANIMATION}>`. */
export const SPRING_DROP_ANIMATION: DropAnimation = createSpringDropAnimation();

/**
 * Lands a drag overlay on a node the caller names, for surfaces where the
 * lifted node and the landed node differ (see the module comment). Call it
 * SYNCHRONOUSLY in the drag-end handler, before the state change that
 * unmounts the overlay: it clones the overlay in place at once, then waits
 * for the landing node to appear (or, with `expectMove`, to move away from
 * where it was) and flies the clone onto it. A landing that never shows up
 * fades the clone instead. Under reduced motion nothing is cloned. Returns a
 * cancel that removes the clone and restores the landing node at once.
 *
 * `findTarget` runs on every frame of the wait, so it may return the node
 * that is about to unmount on the first frames — `expectMove` is how a
 * committed move tells the clone to wait for the re-render rather than land
 * on the stale slot. A rejected or cancelled drop passes `expectMove: false`
 * and lands straight back where it came from.
 */
export function settleOverlay({
  overlay,
  findTarget,
  velocity = ZERO,
  expectMove = false,
}: {
  overlay: HTMLElement;
  findTarget: () => HTMLElement | null;
  velocity?: Vec2;
  expectMove?: boolean;
}): () => void {
  if (motionReduced() || typeof document === "undefined") return noop;
  const rect = overlay.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return noop;
  const origin = findTarget()?.getBoundingClientRect() ?? null;

  // A visual ghost only: no id, no dnd marker, no place in the accessibility
  // tree or the tab order, and nothing under it takes its presses.
  const clone = overlay.cloneNode(true) as HTMLElement;
  clone.removeAttribute("id");
  clone.removeAttribute("data-dnd-overlay");
  clone.setAttribute("data-drop-settle", "");
  clone.setAttribute("aria-hidden", "true");
  clone.inert = true;
  clone.style.position = "fixed";
  clone.style.left = `${rect.left}px`;
  clone.style.top = `${rect.top}px`;
  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  clone.style.margin = "0";
  clone.style.pointerEvents = "none";
  clone.style.transformOrigin = "0 0";
  // Above dnd-kit's overlay layer (999): the clone stands in for it.
  clone.style.zIndex = "1000";
  document.body.appendChild(clone);

  const started = performance.now();
  const same = (a: DOMRect, b: DOMRect) =>
    Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5;

  let done = false;
  let restoreTarget: (() => void) | null = null;
  const finish = () => {
    if (done) return;
    done = true;
    restoreTarget?.();
    clone.remove();
  };
  const fadeOut = () => {
    if (typeof clone.animate !== "function") return finish();
    const fade = clone.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 120,
      easing: "linear",
      fill: "forwards",
    });
    fade.onfinish = finish;
    fade.oncancel = finish;
  };
  const land = (target: HTMLElement) => {
    const targetRect = target.getBoundingClientRect();
    const to: SettleTransform = {
      x: targetRect.left - rect.left,
      y: targetRect.top - rect.top,
      scaleX: targetRect.width / rect.width,
      scaleY: targetRect.height / rect.height,
    };
    // Released exactly where it landed: nothing to fly, so nothing to hide.
    if (same(rect, targetRect) && Math.abs(to.scaleX - 1) < 0.01 && Math.abs(to.scaleY - 1) < 0.01) {
      return finish();
    }
    const previousVisibility = target.style.visibility;
    target.style.visibility = "hidden";
    restoreTarget = () => {
      target.style.visibility = previousVisibility;
    };
    void animateSettle(clone, { x: 0, y: 0, scaleX: 1, scaleY: 1 }, to, velocity).then(finish);
  };

  const tick = () => {
    if (done) return;
    const target = findTarget();
    const waited = performance.now() - started;
    if (target) {
      const moved =
        !expectMove || origin === null || !same(origin, target.getBoundingClientRect());
      if (moved || waited >= LANDING_WAIT_MS) return land(target);
    } else if (waited >= LANDING_WAIT_MS) {
      return fadeOut();
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
  // The surface that started the settle cancels it when it unmounts, so a
  // board torn down mid-flight leaves no ghost behind.
  return finish;
}

function noop(): void {}
